/**
 * Armazenamento seguro que encapsula localStorage, sessionStorage e cookies.
 * Usado apenas para preferências não-sensíveis (ex.: email lembrado).
 * JWT NÃO deve ser armazenado aqui (Sprint 3 — cookie HttpOnly via BFF).
 */

import { parseCookies, setCookie, destroyCookie } from "nookies";
import CryptoJS from "crypto-js";

const getEncryptionKey = () => {
  return (
    (typeof window !== "undefined"
      ? window.location.hostname
      : "meu-barbeiro-pro") + "-storage-key"
  );
};

const encrypt = (text: string): string => {
  try {
    return CryptoJS.AES.encrypt(text, getEncryptionKey()).toString();
  } catch {
    console.error("Erro ao criptografar dados");
    return "";
  }
};

const decrypt = (encryptedText: string): string => {
  try {
    const bytes = CryptoJS.AES.decrypt(encryptedText, getEncryptionKey());
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch {
    console.error("Erro ao descriptografar dados");
    return "";
  }
};

export const secureStorage = {
  set: (
    key: string,
    value: string,
    options: {
      expires?: number | Date;
      persistent?: boolean;
      useCookie?: boolean;
    } = {}
  ) => {
    const { expires, persistent = true, useCookie = false } = options;

    const data = JSON.stringify({
      value,
      expires: expires
        ? Date.now() + (typeof expires === "number" ? expires * 1000 : 0)
        : null,
    });

    const encrypted = encrypt(data);

    try {
      if (typeof window !== "undefined") {
        const storage = persistent ? localStorage : sessionStorage;
        storage.setItem(key, encrypted);
      }
    } catch {
      console.error("Erro ao armazenar dados");
    }

    if (useCookie) {
      const cookieOptions: {
        maxAge?: number;
        path: string;
        sameSite: "lax";
      } = {
        path: "/",
        sameSite: "lax",
      };

      if (typeof expires === "number") {
        cookieOptions.maxAge = expires;
      }

      setCookie(undefined, key, encrypted, cookieOptions);
    }
  },

  get: (key: string, options: { checkCookie?: boolean } = {}): string | null => {
    const { checkCookie = false } = options;
    let encrypted: string | null = null;

    if (typeof window !== "undefined") {
      encrypted = localStorage.getItem(key) || sessionStorage.getItem(key);
    }

    if (!encrypted && checkCookie) {
      const cookies = parseCookies();
      encrypted = cookies[key] || null;
    }

    if (!encrypted) return null;

    try {
      const decrypted = decrypt(encrypted);
      if (!decrypted) return null;

      const data = JSON.parse(decrypted) as {
        value: string;
        expires: number | null;
      };

      if (data.expires && Date.now() > data.expires) {
        secureStorage.remove(key, { removeCookie: checkCookie });
        return null;
      }

      return data.value;
    } catch {
      return null;
    }
  },

  remove: (
    key: string,
    options: { removeCookie?: boolean } = {}
  ) => {
    const { removeCookie = false } = options;

    if (typeof window !== "undefined") {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    }

    if (removeCookie) {
      destroyCookie(undefined, key, { path: "/" });
    }
  },
};
